import { useMemo, useState } from 'react';
import { Search, Boxes, Package, RefreshCw, Eye } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { LoadingTable } from '../components/ui/loading-states';
import { ErrorState } from '../components/ui/error-state';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  useMarkirovkaStock,
  useMarkirovkaGtinMarks,
  useTriggerMarkirovkaStockSync,
} from '../hooks/useMarkirovkaStock';
import type { MarkirovkaStockGtin } from '../types/markirovka-stock.types';

export function MarkirovkaStock() {
  const { data, isLoading, error, refetch, isFetching } = useMarkirovkaStock();
  const syncMutation = useTriggerMarkirovkaStockSync();
  const [search, setSearch] = useState('');
  const [selectedGtin, setSelectedGtin] = useState<MarkirovkaStockGtin | null>(
    null,
  );

  const syncedAtLabel = data?.synced_at
    ? new Date(data.synced_at).toLocaleString('ru-RU')
    : null;

  const rows = data?.data ?? [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.gtin.toLowerCase().includes(q) ||
        (r.name || '').toLowerCase().includes(q),
    );
  }, [rows, search]);

  return (
    <div className="space-y-6">
      {/* Заголовок */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Остатки маркировки
          </h1>
          <p className="text-muted-foreground">
            Маркированные медикаменты организации из edo.markirovka.kz
          </p>
          {syncedAtLabel && (
            <p className="text-xs text-muted-foreground mt-1">
              Обновлено: {syncedAtLabel}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`}
            />
            Обновить
          </Button>
          <Button
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${syncMutation.isPending ? 'animate-spin' : ''}`}
            />
            {syncMutation.isPending
              ? 'Синхронизация…'
              : 'Синхронизировать из edo'}
          </Button>
        </div>
      </div>
      {syncMutation.isSuccess && (
        <p className="text-sm text-muted-foreground">
          Синхронизация запущена в фоне. Список обновится автоматически через
          несколько секунд.
        </p>
      )}

      {/* Итоги */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Наименований (GTIN)
            </CardTitle>
            <Boxes className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{rows.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Всего КМ на остатках
            </CardTitle>
            <Package className="w-4 h-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data?.total_count ?? 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Таблица */}
      <Card>
        <CardHeader>
          <CardTitle>Товары на остатках</CardTitle>
          <CardDescription>
            Нажмите на строку, чтобы посмотреть коды маркировки (КМ).
          </CardDescription>
          <div className="relative mt-2 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Поиск по GTIN или названию"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingTable rows={6} columns={3} />
          ) : error ? (
            <ErrorState
              title="Не удалось загрузить остатки"
              description={
                (error as Error)?.message ||
                'Проверьте, что интеграция с edo.markirovka.kz настроена в Настройках.'
              }
              onAction={() => refetch()}
            />
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              {rows.length === 0
                ? 'Нет товаров на остатках'
                : 'Ничего не найдено по запросу'}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>GTIN</TableHead>
                  <TableHead>Наименование</TableHead>
                  <TableHead className="text-right">Количество КМ</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((row) => (
                  <TableRow
                    key={row.gtin}
                    className="cursor-pointer"
                    onClick={() => setSelectedGtin(row)}
                  >
                    <TableCell className="font-mono text-sm">
                      {row.gtin}
                    </TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell className="text-right">
                      <Badge variant="secondary">{row.count}</Badge>
                    </TableCell>
                    <TableCell>
                      <Eye className="w-4 h-4 text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <GtinMarksDialog
        gtin={selectedGtin}
        onClose={() => setSelectedGtin(null)}
      />
    </div>
  );
}

interface GtinMarksDialogProps {
  gtin: MarkirovkaStockGtin | null;
  onClose: () => void;
}

function GtinMarksDialog({ gtin, onClose }: GtinMarksDialogProps) {
  const { data, isLoading, error, refetch } = useMarkirovkaGtinMarks(
    gtin?.gtin ?? null,
  );

  return (
    <Dialog open={!!gtin} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{gtin?.name}</DialogTitle>
          <DialogDescription>
            GTIN {gtin?.gtin} — коды маркировки на остатках
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <LoadingTable rows={5} columns={4} />
        ) : error ? (
          <ErrorState
            title="Не удалось загрузить КМ"
            description={(error as Error)?.message}
            onAction={() => refetch()}
          />
        ) : (data?.data?.length ?? 0) === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Нет кодов маркировки
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>CIS</TableHead>
                <TableHead>Партия</TableHead>
                <TableHead>Годен до</TableHead>
                <TableHead>Статус</TableHead>
                <TableHead>Накладная</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.data.map((m) => (
                <TableRow key={m.cis}>
                  <TableCell className="font-mono text-xs break-all max-w-[280px]">
                    {m.cis}
                  </TableCell>
                  <TableCell>{m.party_num || '—'}</TableCell>
                  <TableCell>{m.limit_date || '—'}</TableCell>
                  <TableCell>
                    {m.state_name ? (
                      <Badge variant="outline">{m.state_name}</Badge>
                    ) : (
                      (m.state_id ?? '—')
                    )}
                  </TableCell>
                  <TableCell className="text-xs">
                    {m.paper_num || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}
